
import Vue, { PropType } from 'vue';

import type { ITreeNode } from '../Itree-node';
import type { ITreeNodeEvent } from '../Itree-node-event';
import TreeViewNode from './tux-tree-view-node.vue';
import TuxTreeViewSearch from './tux-tree-view-search.vue';
import * as helpers from '../helpers/tree-node-helpers';
import { TreeNodeEventType } from '../tree-node-event-type';
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';

export default Vue.extend({
	name: 'TreeView',
	components: {
		TreeViewNode,
		TuxTreeViewSearch,
		DynamicScroller,
		DynamicScrollerItem
	},
	props: {
		value: {
			type: Array as PropType<Array<ITreeNode>>,
			required: false,
			default: () => []
		},
		selectedNodeId: {
			type: Number,
			required: false,
			default: null
		},
		searchBoxClass: {
			type: String,
			required: false,
			default: ''
		},
		draggable: {
			type: Boolean,
			required: false,
			default: false
		},
		allowMultipleRootNodes: {
			type: Boolean,
			required: false,
			default: true
		},
		preventNodeClickOverride: {
			type: Boolean,
			required: false,
			default: false
		},
		openFirstVisibleNode: {
			type: Boolean,
			required: false,
			default: true
		},
		virtualScrollHeight: {
			type: Number,
			required: false,
			default: 0
		},
		initializeNodesOnValueChange: {
			type: Boolean,
			required: false,
			default: true
		},
		removeSearchDebounce: {
			type: Boolean,
			required: false,
			default: false
		}
	},
	data() {
		return {
			enums: { TreeNodeEventType },
			treeNodes: [] as ITreeNode[],
			currentPage: 1 as number,
			searchText: '' as string,
			isExpanded: false as boolean,
			RESIZE_EVENT_NAME: 'resize' as string,
			SEARCH_DEBOUNCE_DEFAULT: 200 as number
		};
	},
	computed: {
		showCollapseAll(): boolean {
			return this.treeNodes.filter(node => node.children.length > 0).length > 0;
		},
		visibleNodes(): ITreeNode[] {
			return this.treeNodes.filter(x => x.isVisible);
		}
	},
	watch: {
		value: function(value: ITreeNode[]) {
			if (this.initializeNodesOnValueChange) {
				this.initializeTreeNodes(value);
			}
			// apply search, if any
			if (this.searchText !== '') helpers.doSearch(this.searchText, this.treeNodes);
		},
		searchText: function(value: string) {
			if (this.removeSearchDebounce) helpers.doSearch(value, this.treeNodes);
			else {
				setTimeout(() => {
					helpers.doSearch(value, this.treeNodes);
				}, this.SEARCH_DEBOUNCE_DEFAULT);
			}
		}
	},
	mounted() {
		this.initializeTreeNodes(this.value);
	},
	methods: {
		toggleExpanded() {
			this.isExpanded = !this.isExpanded;
			helpers.walkTreeNodes(this.treeNodes, (n: ITreeNode) => {
				n.isOpened = this.isExpanded;
				return true;
			});
		},
		initializeTreeNodes(treeNodes: ITreeNode[]) {
			// clone the passed in nodes so the tree can own their behavior
			this.treeNodes = helpers.getWrappedTreeNodes(treeNodes);
			this.isExpanded = false;

			helpers.initializeTreeNodes(this.selectedNodeId, this.openFirstVisibleNode, this.treeNodes);
		},
		openInitialNode(nodeId: number) {
			helpers.walkTreeNodes(this.treeNodes, (n: ITreeNode, subResults?: boolean[]) => {
				const childIsSelected: boolean = subResults !== undefined && subResults.filter(t => t).length > 0;
				const selected: boolean = n.id === nodeId;
				if (selected) {
					n.isSelected = true;
				}
				if (childIsSelected) {
					n.isOpened = true;
				}
				return childIsSelected || selected;
			});
		},
		handleTreeNodeEvent(event: ITreeNodeEvent): void {
			helpers.handleTreeNodeEvent(event, this.treeNodes, this.preventNodeClickOverride);
			this.$emit('tree-node-event', event);
		},
		openTreeNode(node: ITreeNode | undefined) {
			if (node !== undefined) {
				node.isOpened = true;
			}
		},
	}
});
